const { calculateStartDate, generatePaymentPlanData } = require('../../payment-plans/tools')
const { smartId } = require('../../tools')
const statementXlsExport = require('./statement-xls-export')
const sumBy = require('lodash/sumBy')
const { format, differenceInCalendarISOWeeks } = require('date-fns')
const { MEMBERSHIPS } = require('../../location/tools/constants')
const { DIRECT_ORDER_TYPES } = require('../../allocation/config')
const get = require('lodash/get')
const groupBy = require('lodash/groupBy')

const calculateManagedCreditFee = (loanLimit) => {
  let managedCredit = 0
  if (loanLimit > 0 && loanLimit <= 50000) {
    managedCredit = 500
  } else if (loanLimit > 50000 && loanLimit <= 150000) {
    managedCredit = 1300
  } else if (loanLimit > 150000 && loanLimit <= 500000) {
    managedCredit = 3250
  } else if (loanLimit > 500000) {
    managedCredit = 5000
  }

  return managedCredit
}

const groupTransactions = (transactionList, separateServiceFeeAndPrincipal) => {
  return transactionList.reduce((acc, transaction) => {
    const dueDate = new Date(transaction.due_date)
    const startDate = calculateStartDate(dueDate, transaction.paymentOption.frequency)
    const { state } = smartId.parse(transaction.fsid)
    const { paymentPlan, pastDue, serviceFeeAmount, percentageFee } = generatePaymentPlanData({
      invoiceData: transaction,
      market: state,
      isPayPerUseClient: transaction.pay_per_use,
      feePercentage: transaction.fee_percentage,
      feePercentageLongTerm: transaction.fee_percentage_long_term,
      startDate,
      term: transaction.paymentOption.term,
      frequency: transaction.paymentOption.frequency,
      separateServiceFeeAndPrincipal,
      serviceFeePercentage: transaction.paymentOption.service_fee_percentage
    })
    transaction.paymentPlan = paymentPlan
    transaction.pastDue = pastDue
    transaction.serviceFeeAmount = serviceFeeAmount
    transaction.percentageFee = percentageFee
    acc[transaction.company_code].push(transaction)
    return acc
  }, {
    KE: [],
    NG: []
  })
}

const calculateBalance = ({
  transactions,
  pinnedTransactions = [],
  loanLimit = null,
  provisionalOrderAmount = null,
  pendingShipments = [],
  serviceFee = 0,
  vatPercentage = 0,
  futureInstallments = [],
  membershipType = null
}) => {
  const debits = transactions
    .filter(transaction => transaction.posting_type === 'debit')
    .map(transaction => ({ ...transaction, amount: -transaction.amount }))

  const credits = transactions
    .filter(transction => transction.posting_type === 'credit')
    .filter(transaction => (transaction.payment_plan && transaction.payment_plan.is_active) || !transaction.payment_plan)
  const pendingCredits = transactions
    .filter(transaction => transaction.posting_type === 'pending-credit')
  const instalmentBalance = futureInstallments.reduce((balance, installment) => {
    const overdue = installment.amount - installment.amount_paid
    balance += overdue
    return balance
  }, 0)

  const creditsBalance = sumBy(credits, 'amount')
  const debitsBalance = sumBy(debits, 'amount')
  const pendingCreditsBalance = sumBy(pendingCredits, 'amount')
  const totalBalance = (creditsBalance + debitsBalance)
  const pendingBalance = totalBalance + pendingCreditsBalance
  const overdueBalance = pinnedTransactions
    .filter(transaction => format(transaction.due_date, 'YYYY-MM-DD') < format(new Date(), 'YYYY-MM-DD'))
    .reduce((balance, transaction) => {
      const overdue = transaction.amount - transaction.amount_paid
      balance += overdue
      return balance
    }, 0)
  const pendingShipmentsAmount = pendingShipments.reduce((acc, prods) => {
    const vatAmount = prods.vat ? (prods.price * vatPercentage) / 100 : 0
    if (membershipType === MEMBERSHIPS.CLASSIC && (prods.subscription_type !== DIRECT_ORDER_TYPES.PAY_ON_DELIVERY)) {
      return acc
    }
    acc = acc + prods.amount + vatAmount
    return acc
  }, 0)
  const addServiceFee = pendingShipmentsAmount || provisionalOrderAmount ? serviceFee : 0
  const loanLimitBalance = loanLimit + pendingBalance - (provisionalOrderAmount + pendingShipmentsAmount + addServiceFee + instalmentBalance)
  const pendingDeliveries = pendingShipmentsAmount + provisionalOrderAmount

  return {
    balance: totalBalance,
    overdue: overdueBalance,
    pending: pendingBalance,
    loanLimit,
    loanBalance: loanLimitBalance,
    pendingDeliveries,
    instalmentBalance
  }
}

const getCreditInfo = ({
  loanLimit,
  orderStatus,
  totalOutstandingAmount,
  pendingDeliveriesAmount,
  arBalance,
  lrBalance,
  creditApplicationStatus,
  balances,
  loanDeliveryAmountUsed,
  has60DayPaymentOption,
  has90DayPaymentOption
}) => {
  const cashBalance = (arBalance + lrBalance)
  return {
    creditLimit: loanLimit,
    creditCustomer: !!(loanLimit && loanLimit > 0),
    orderStatus,
    availableCreditBalance: (loanLimit || 0) - loanDeliveryAmountUsed - totalOutstandingAmount,
    pendingDeliveriesAmount,
    cashBalance,
    creditApplicationStatus,
    balances,
    has60DayPaymentOption,
    has90DayPaymentOption
  }
}

const groupedInstalments = (instalments) => {
  const groupedInstalments = groupBy(instalments, 'payment_plan.id')
  return Object.values(groupedInstalments).map((paymentPlanInstalments) => {
    const paymentPlan = get(paymentPlanInstalments[0], 'payment_plan')
    let totalOutstandingAmount = 0
    let pendingOutstandingAmount = 0
    paymentPlan.startDate = format(paymentPlan.start_date, 'DD/MM/YYYY')
    paymentPlan.installments = paymentPlanInstalments.map(i => {
      delete i.payment_plan
      i.outstandingAmount = i.amount - i.amount_paid
      i.pendingOutstandingAmount = i.outstandingAmount - (i.pending_payment_amount || 0)
      totalOutstandingAmount += i.outstandingAmount
      pendingOutstandingAmount += i.pendingOutstandingAmount
      i.is_paid = i.amount_paid >= i.amount
      return i
    })
    paymentPlan.totalOutstandingAmount = totalOutstandingAmount
    paymentPlan.pendingOutstandingAmount = pendingOutstandingAmount
    paymentPlan.is_paid = paymentPlan.totalOutstandingAmount <= 0
    return {
      ...paymentPlan,
      installments: paymentPlanInstalments
    }
  })
}

const PAYMENT_WEEKS = {
  CURRENT_WEEK: '0',
  NEXT_WEEK: '1',
  OVERDUE: 'overdue'
}

// A type of instalment with just 1 term
const isFixedTermPayment = (transaction) => {
  if (!transaction || !transaction.payment_plan || transaction.txn_type !== 'installment') {
    return false
  }
  return transaction.payment_plan.term === 1
}

const isFutureFixedTermPayment = (dueDate) => {
  return differenceInCalendarISOWeeks(dueDate, new Date()) > 0
}

const getPayableDetails = (payables) => {
  if (!payables) {
    return
  }

  // if no payable listed for current week we add a default placeholder.
  // This is needed to display fixed term payments
  if (!payables[PAYMENT_WEEKS.CURRENT_WEEK]) {
    payables[PAYMENT_WEEKS.CURRENT_WEEK] = { week: '0', totaldue: 0, transactions: [] }
  }

  const paymentList = Object.values(payables)

  return paymentList.reduce((data, payment) => {
    // Payment due this week (payment could have multiple transactions ie invoice and instalment)
    if (payment.week === PAYMENT_WEEKS.CURRENT_WEEK) {
      data.currentWeek.push(payment)
    }

    // Payment due next week (payment could have multiple transactions ie invoice and instalment)
    if (payment.week === PAYMENT_WEEKS.NEXT_WEEK) {
      const nextWeekTransactions = []
      let nextWeekTotalDue = 0

      payment.transactions.forEach(transaction => {
        const { amount_paid: amountPaid, amount } = transaction
        const amountDue = (amount - amountPaid)
        if (!isFixedTermPayment(transaction)) {
          nextWeekTransactions.push(transaction)
          nextWeekTotalDue += amountDue
        }
      })

      data.nextWeek.push({
        ...payment,
        transactions: nextWeekTransactions,
        totaldue: nextWeekTotalDue
      })
    }

    // Future payments. This includes all payments that are NOT part of the current week's (this week) payments
    // Future payments = next week payments and all other subsequent week payments
    // We have an exception though, every fixed-term payment should be visible on the overview page (ie current week).
    if (Number(payment.week) >= Number(PAYMENT_WEEKS.NEXT_WEEK)) {
      const futureWeekTransactions = []
      let futureWeekTotalDue = 0

      payment.transactions.forEach(transaction => {
        const {
          due_date: duedate,
          amount_paid: amountPaid,
          amount
        } = transaction

        const amountDue = (amount - amountPaid)

        if (isFixedTermPayment(transaction) && isFutureFixedTermPayment(duedate)) {
          const currentWeekPayments = data.currentWeek[0]
          currentWeekPayments.totaldue += amountDue
          currentWeekPayments.transactions.push(transaction)
        } else {
          futureWeekTransactions.push(transaction)
          futureWeekTotalDue += amountDue
        }
      })

      data.futureWeeks.push({
        ...payment,
        transactions: futureWeekTransactions,
        totaldue: futureWeekTotalDue
      })
    }

    // Overdue payments (it could have multiple transactions ie invoices and instalments)
    if (payment.week === PAYMENT_WEEKS.OVERDUE && payment.totaldue > 0) {
      data.overduePayments.push(payment)
    }

    payment.transactions.map(transaction => {
      transaction.amount_due = transaction.amount - transaction.amount_paid

      if (transaction.txn_type === 'invoice') {
        data.invoices.push(transaction)
      } else {
        data.instalments.push(transaction)
      }
    })
    return data
  }, {
    currentWeek: [],
    nextWeek: [],
    futureWeeks: [],
    overduePayments: [],
    invoices: [],
    instalments: []
  })
}

module.exports = {
  calculateManagedCreditFee,
  statementXlsExport,
  groupTransactions,
  calculateBalance,
  getCreditInfo,
  groupedInstalments,
  getPayableDetails
}
